/**
 * PCM conversion helpers, split out of mic.ts so both the mic pipeline and the
 * utterance segmenter can share them without a circular import.
 */
import { SAMPLE_RATE } from "./vad";

/** Convert one frame of `[-1, 1]` floats to 16-bit signed little-endian. */
export function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]!));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Pack a raw Int16 16 kHz mono buffer as a WAV blob — for enrollment uploads. */
export function pcm16ToWavBlob(samples: Int16Array, sampleRate = SAMPLE_RATE): Blob {
  const byteRate = sampleRate * 2;
  const buffer = new ArrayBuffer(44 + samples.byteLength);
  const view = new DataView(buffer);
  let p = 0;
  const ws = (s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(p++, s.charCodeAt(i)); };
  ws("RIFF"); view.setUint32(p, 36 + samples.byteLength, true); p += 4;
  ws("WAVEfmt "); view.setUint32(p, 16, true); p += 4;
  view.setUint16(p, 1, true); p += 2;            // PCM
  view.setUint16(p, 1, true); p += 2;            // mono
  view.setUint32(p, sampleRate, true); p += 4;
  view.setUint32(p, byteRate, true); p += 4;
  view.setUint16(p, 2, true); p += 2;            // block align
  view.setUint16(p, 16, true); p += 2;           // bits/sample
  ws("data"); view.setUint32(p, samples.byteLength, true); p += 4;
  new Int16Array(buffer, 44).set(samples);
  return new Blob([buffer], { type: "audio/wav" });
}
